import { useState, useEffect, useMemo } from 'react';
import {
  Zap, Plus, Trash2, RefreshCw, Play, Eye, AlertTriangle,
  CheckCircle, Clock, TrendingUp, TrendingDown, Loader2, X, Save, Shield, Search,
} from 'lucide-react';
import FeatureGate from '../components/FeatureGate';
import { Spinner, InfoTip } from '../components/ui';
import { useActivePolling } from '../hooks/useActivePolling';
import {
  listArbitrageSchedules, saveArbitrageSchedule, deleteArbitrageSchedule,
  scanArbitrageOpportunity, executeArbitrageNow, getArbitrageHistory,
  getExchangeConnections, getExchangeMarkets,
} from '../services/api';

// Sort pairs so popular bases (BTC/ETH/SOL) on popular quotes (USDT/USDC/USD)
// surface first — same pattern as Algo Orders / Grid Bot / Strategy Builder.
function sortPairs(pairs) {
  const POPULAR_BASES  = ['BTC', 'ETH', 'SOL', 'BNB', 'XRP', 'DOGE', 'ADA', 'AVAX', 'LINK', 'MATIC', 'DOT', 'UNI', 'ATOM', 'NEAR', 'APT', 'ARB', 'OP'];
  const POPULAR_QUOTES = ['USDT', 'USDC', 'USD', 'BUSD', 'DAI'];
  const rank = (p) => {
    const [base, quote] = p.split('/');
    const baseRank  = POPULAR_BASES.indexOf(base);
    const quoteRank = POPULAR_QUOTES.indexOf(quote);
    return (quoteRank === -1 ? 100 : quoteRank) * 1000 + (baseRank === -1 ? 999 : baseRank);
  };
  return [...pairs].sort((a, b) => rank(a) - rank(b) || a.localeCompare(b));
}

const FREQUENCIES = [
  { id: 'manual', label: 'Manual only', desc: 'Only when you click Execute' },
  { id: '5min',   label: 'Every 5 min', desc: 'Worker scans every 5 min' },
  { id: 'hourly', label: 'Hourly',      desc: 'Worker scans once per hour' },
];

function Card({ children, className = '' }) {
  return <div className={`bg-dark-800 rounded-2xl p-6 border border-dark-700/50 ${className}`}>{children}</div>;
}

function Toast({ message, type = 'success', onDismiss }) {
  useEffect(() => { const t = setTimeout(onDismiss, 6000); return () => clearTimeout(t); }, [onDismiss]);
  return (
    <div className={`fixed bottom-6 right-6 z-50 px-5 py-3 rounded-xl shadow-2xl text-sm font-medium flex items-center gap-2
      ${type === 'success' ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30'
        : type === 'warning' ? 'bg-amber-500/20 text-amber-300 border border-amber-500/30'
        : type === 'error' ? 'bg-red-500/20 text-red-300 border border-red-500/30'
        : 'bg-blue-500/20 text-blue-300 border border-blue-500/30'}`}>
      {message}
    </div>
  );
}

const STATUS_STYLES = {
  ok:           { color: 'text-emerald-400', bg: 'bg-emerald-500/15', label: 'CLEAN FILL' },
  partial:      { color: 'text-amber-400',   bg: 'bg-amber-500/15',   label: 'PARTIAL' },
  'partial-leg':{ color: 'text-amber-400',   bg: 'bg-amber-500/15',   label: 'IMBALANCED' },
  'open-leg':   { color: 'text-red-400',     bg: 'bg-red-500/15',     label: 'OPEN LEG ⚠' },
  unfilled:     { color: 'text-gray-400',    bg: 'bg-gray-500/15',    label: 'UNFILLED' },
  'no-trade':   { color: 'text-gray-400',    bg: 'bg-gray-500/15',    label: 'NO TRADE' },
  'rate-limited':{color: 'text-amber-400',   bg: 'bg-amber-500/15',   label: 'RATE LIMITED' },
  skipped:      { color: 'text-gray-400',    bg: 'bg-gray-500/15',    label: 'SKIPPED' },
  error:        { color: 'text-red-400',     bg: 'bg-red-500/15',     label: 'ERROR' },
};

// ─── Editor ──────────────────────────────────────────────────────────────────

function ScheduleEditor({ schedule, exchanges, onSubmit, onCancel, saving }) {
  const isEdit = !!schedule;
  const [name, setName] = useState(schedule?.name || '');
  const [symbol, setSymbol] = useState(schedule?.symbol || 'BTC/USDT');
  const [exchangeA, setExchangeA] = useState(schedule?.exchangeA || (exchanges[0]?.exchangeId || 'binance'));
  const [exchangeB, setExchangeB] = useState(schedule?.exchangeB || (exchanges[1]?.exchangeId || 'kraken'));
  const [minSpreadPct, setMinSpreadPct] = useState(schedule?.minSpreadPct ?? 0.5);
  const [takerFeePct, setTakerFeePct] = useState(schedule?.takerFeePct ?? 0.1);
  const [maxPositionUsd, setMaxPositionUsd] = useState(schedule?.maxPositionUsd ?? 100);
  const [maxTradesPerHour, setMaxTradesPerHour] = useState(schedule?.maxTradesPerHour ?? 6);
  const [iocSlippagePct, setIocSlippagePct] = useState(schedule?.iocSlippagePct ?? 0.1);
  const [frequency, setFrequency] = useState(schedule?.frequency || 'manual');
  const [enabled, setEnabled] = useState(schedule?.enabled ?? false);

  // Intersect the markets from BOTH exchanges so the user can ONLY pick pairs
  // that are actually tradable on both. Otherwise an arb attempt fails at the
  // execution stage with a useless "symbol not found" error.
  const [marketsA, setMarketsA] = useState([]);
  const [marketsB, setMarketsB] = useState([]);
  const [loadingMarkets, setLoadingMarkets] = useState(false);
  const [pairSearch, setPairSearch] = useState('');

  const sameExchange = exchangeA === exchangeB;

  useEffect(() => {
    if (!exchangeA || sameExchange) return;
    let cancelled = false;
    setLoadingMarkets(true);
    getExchangeMarkets(exchangeA)
      .then(res => {
        if (cancelled) return;
        setMarketsA((res?.markets || res?.data?.markets || []).map(m => m.symbol));
      })
      .catch(() => { if (!cancelled) setMarketsA([]); })
      .finally(() => { if (!cancelled) setLoadingMarkets(false); });
    return () => { cancelled = true; };
  }, [exchangeA, sameExchange]);

  useEffect(() => {
    if (!exchangeB || sameExchange) return;
    let cancelled = false;
    setLoadingMarkets(true);
    getExchangeMarkets(exchangeB)
      .then(res => {
        if (cancelled) return;
        setMarketsB((res?.markets || res?.data?.markets || []).map(m => m.symbol));
      })
      .catch(() => { if (!cancelled) setMarketsB([]); })
      .finally(() => { if (!cancelled) setLoadingMarkets(false); });
    return () => { cancelled = true; };
  }, [exchangeB, sameExchange]);

  const sharedPairs = useMemo(() => {
    if (!marketsA.length || !marketsB.length) return [];
    const setB = new Set(marketsB);
    return sortPairs(marketsA.filter(p => setB.has(p)));
  }, [marketsA, marketsB]);

  const filteredPairs = pairSearch
    ? sharedPairs.filter(p => p.toLowerCase().includes(pairSearch.toLowerCase()))
    : sharedPairs.slice(0, 200);

  const pairOnBothExchanges = sharedPairs.includes(symbol);
  const canSave = name.trim() && symbol && !sameExchange && pairOnBothExchanges && Number(minSpreadPct) >= 0.05 && Number(maxPositionUsd) >= 5;

  function submit() {
    if (!canSave) return;
    onSubmit({
      scheduleId: schedule?.scheduleId,
      name: name.trim(),
      symbol: symbol.toUpperCase().trim(),
      exchangeA, exchangeB,
      minSpreadPct: Number(minSpreadPct),
      takerFeePct: Number(takerFeePct),
      maxPositionUsd: Number(maxPositionUsd),
      maxTradesPerHour: Number(maxTradesPerHour),
      iocSlippagePct: Number(iocSlippagePct),
      frequency,
      enabled: !!enabled,
    });
  }

  return (
    <Card>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider">{isEdit ? 'Edit schedule' : 'New schedule'}</h3>
        <button onClick={onCancel} className="text-gray-500 hover:text-white"><X className="w-4 h-4" /></button>
      </div>

      <div className="bg-amber-500/5 border border-amber-500/20 rounded-xl px-4 py-3 mb-4 flex items-start gap-3">
        <Shield className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-amber-300 text-sm font-semibold">High-risk feature — opt in carefully</p>
          <p className="text-amber-300/80 text-xs mt-0.5">
            Cross-exchange arbitrage fires real IOC orders on both exchanges in parallel. If one leg fills and the other doesn't, you'll be left with a one-sided position
            (we mark this <strong>OPEN LEG</strong> in history and notify you). Start with a small <code>maxPositionUsd</code> and a high <code>minSpreadPct</code>.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
        <div>
          <label className="block text-xs text-gray-500 mb-1.5">Schedule name</label>
          <input value={name} onChange={e => setName(e.target.value)} placeholder="BTC arb across CEXes" maxLength={64}
            className="w-full bg-dark-700 border border-dark-600 rounded-xl px-3 py-2 text-white text-sm focus:border-accent-primary focus:outline-none" />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1.5">
            Symbol {symbol && !pairOnBothExchanges && sharedPairs.length > 0 && (
              <span className="text-red-400 ml-1">— not tradable on both</span>
            )}
          </label>
          <input value={symbol} onChange={e => setSymbol(e.target.value.toUpperCase())} placeholder="BTC/USDT"
            className={`w-full bg-dark-700 border ${symbol && !pairOnBothExchanges && sharedPairs.length > 0 ? 'border-red-500/50' : 'border-dark-600'} rounded-xl px-3 py-2 text-white text-sm font-mono focus:border-accent-primary focus:outline-none`} />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1.5">Exchange A</label>
          <select value={exchangeA} onChange={e => setExchangeA(e.target.value)}
            className="w-full bg-dark-700 border border-dark-600 rounded-xl px-3 py-2 text-white text-sm focus:border-accent-primary focus:outline-none">
            {exchanges.map(ex => <option key={ex.exchangeId} value={ex.exchangeId}>{ex.name || ex.exchangeId}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1.5">Exchange B</label>
          <select value={exchangeB} onChange={e => setExchangeB(e.target.value)}
            className={`w-full bg-dark-700 border ${sameExchange ? 'border-red-500/50' : 'border-dark-600'} rounded-xl px-3 py-2 text-white text-sm focus:border-accent-primary focus:outline-none`}>
            {exchanges.map(ex => <option key={ex.exchangeId} value={ex.exchangeId}>{ex.name || ex.exchangeId}</option>)}
          </select>
          {sameExchange && <p className="text-xs text-red-400 mt-1">Must be different from Exchange A</p>}
        </div>

        {/* Searchable per-exchange pair list — shows only pairs that exist on
            BOTH exchanges. Same UX pattern as Strategy Builder / Grid Bot. */}
        {!sameExchange && (
          <div className="sm:col-span-2">
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-xs text-gray-500">
                Pairs tradable on both exchanges{' '}
                {loadingMarkets && <Loader2 className="w-3 h-3 inline animate-spin ml-1" />}
              </label>
              <span className="text-[10px] text-gray-500">
                {loadingMarkets ? 'Loading…' : `${sharedPairs.length.toLocaleString()} shared`}
              </span>
            </div>
            <div className="relative">
              <Search className="w-4 h-4 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                value={pairSearch}
                onChange={e => setPairSearch(e.target.value)}
                placeholder={loadingMarkets ? 'Loading both exchange markets…' : 'Search pair (e.g. BTC, ETH/USDC)'}
                className="w-full bg-dark-700 border border-dark-600 rounded-xl pl-9 pr-3 py-2 text-white text-sm focus:border-accent-primary focus:outline-none mb-2"
              />
            </div>
            <div className="max-h-40 overflow-y-auto bg-dark-700/40 border border-dark-600 rounded-xl">
              {loadingMarkets ? (
                <div className="p-3 text-xs text-gray-500 flex items-center gap-2">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" /> Fetching markets from {exchangeA} + {exchangeB}…
                </div>
              ) : sharedPairs.length === 0 ? (
                <div className="p-3 text-xs text-amber-400">No shared pairs found. Either the exchanges expose different markets or markets failed to load.</div>
              ) : filteredPairs.length === 0 ? (
                <div className="p-3 text-xs text-gray-500">No pairs match "{pairSearch}"</div>
              ) : (
                filteredPairs.map(p => (
                  <button
                    key={p}
                    type="button"
                    onClick={() => setSymbol(p)}
                    className={`block w-full text-left px-3 py-1.5 text-sm font-mono transition-colors ${
                      symbol === p
                        ? 'bg-accent-primary/15 text-accent-primary'
                        : 'text-gray-300 hover:bg-dark-600'
                    }`}
                  >
                    {p}
                  </button>
                ))
              )}
            </div>
            <p className="text-[10px] text-gray-500 mt-1">
              {pairSearch
                ? `${filteredPairs.length} match${filteredPairs.length === 1 ? '' : 'es'}`
                : sharedPairs.length > 200
                  ? `Showing top 200 — search to find more`
                  : `${sharedPairs.length} pair${sharedPairs.length === 1 ? '' : 's'} on both`}
            </p>
          </div>
        )}
        <div>
          <label className="text-xs text-gray-500 mb-1.5 flex items-center gap-1.5">
            Min net spread % (after fees)
            <InfoTip side="top">Minimum profit spread AFTER both taker fees you require before firing. Math: 0.5% net + 0.2% combined fees = 0.7% GROSS spread required. Most liquid pairs rarely exceed this. Start conservative (0.5%); decrease only after consistent fills.</InfoTip>
          </label>
          <input type="number" value={minSpreadPct} onChange={e => setMinSpreadPct(e.target.value)} step={0.05} min={0.05} max={20}
            className="w-full bg-dark-700 border border-dark-600 rounded-xl px-3 py-2 text-white text-sm focus:border-accent-primary focus:outline-none" />
        </div>
        <div>
          <label className="text-xs text-gray-500 mb-1.5 flex items-center gap-1.5">
            Taker fee % (per leg)
            <InfoTip side="top">YOUR actual taker fee on each exchange (default 0.1% = retail tier). VIP tiers can be 0.05% or lower. CRITICAL: misconfiguring this makes the bot fire unprofitable trades. Verify your actual fee on the exchange's fee schedule.</InfoTip>
          </label>
          <input type="number" value={takerFeePct} onChange={e => setTakerFeePct(e.target.value)} step={0.01} min={0} max={5}
            className="w-full bg-dark-700 border border-dark-600 rounded-xl px-3 py-2 text-white text-sm focus:border-accent-primary focus:outline-none" />
        </div>
        <div>
          <label className="text-xs text-gray-500 mb-1.5 flex items-center gap-1.5">
            Max position per attempt ($)
            <InfoTip side="top">Capital committed to ONE arb attempt. Start with $20-50 for your first deploys. If an OPEN LEG fires at $1,000, you have $1,000 of one-sided exposure to manually resolve. Small attempts = small blast radius.</InfoTip>
          </label>
          <input type="number" value={maxPositionUsd} onChange={e => setMaxPositionUsd(e.target.value)} min={5} step={5}
            className="w-full bg-dark-700 border border-dark-600 rounded-xl px-3 py-2 text-white text-sm focus:border-accent-primary focus:outline-none" />
        </div>
        <div>
          <label className="text-xs text-gray-500 mb-1.5 flex items-center gap-1.5">
            Max trades / hour
            <InfoTip side="top">Rate limiter. Default 6 = one every 10 minutes max. Prevents runaway firing during sustained spread anomalies (often caused by exchange outage, not real opportunity). Lower = safer; higher = catches more brief windows.</InfoTip>
          </label>
          <input type="number" value={maxTradesPerHour} onChange={e => setMaxTradesPerHour(e.target.value)} min={1} max={60}
            className="w-full bg-dark-700 border border-dark-600 rounded-xl px-3 py-2 text-white text-sm focus:border-accent-primary focus:outline-none" />
        </div>
        <div>
          <label className="text-xs text-gray-500 mb-1.5 flex items-center gap-1.5">
            IOC slippage tolerance %
            <InfoTip side="top">IOC = Immediate-Or-Cancel. The bot prices orders INSIDE your tolerance from best bid/ask. Tight (0.05%) = fewer fills but better prices. Wide (0.2%) = more fills + more OPEN LEG risk. 0.1-0.15% is the realistic range.</InfoTip>
          </label>
          <input type="number" value={iocSlippagePct} onChange={e => setIocSlippagePct(e.target.value)} step={0.05} min={0} max={5}
            className="w-full bg-dark-700 border border-dark-600 rounded-xl px-3 py-2 text-white text-sm focus:border-accent-primary focus:outline-none" />
        </div>
        <div>
          <label className="text-xs text-gray-500 mb-1.5 flex items-center gap-1.5">
            Frequency
            <InfoTip side="top">Manual = scans only when you click Execute (SAFEST for learning). 5min = worker scans every 5 min. Hourly = once per hour. Start Manual until you've seen 5+ successful clean fills; only then consider automating.</InfoTip>
          </label>
          <select value={frequency} onChange={e => setFrequency(e.target.value)}
            className="w-full bg-dark-700 border border-dark-600 rounded-xl px-3 py-2 text-white text-sm focus:border-accent-primary focus:outline-none">
            {FREQUENCIES.map(f => <option key={f.id} value={f.id}>{f.label} — {f.desc}</option>)}
          </select>
        </div>
      </div>

      <label className="flex items-center gap-2 mb-4 cursor-pointer">
        <input type="checkbox" checked={enabled} onChange={e => setEnabled(e.target.checked)} className="accent-accent-primary" />
        <span className="text-xs text-gray-400">
          Enabled (worker may execute it on its frequency).
          <strong className="text-amber-400 ml-1">Default OFF</strong> — flip on only after a successful manual scan + execute.
        </span>
      </label>

      <div className="flex justify-end gap-3">
        <button onClick={onCancel} className="px-4 py-2 rounded-xl text-sm text-gray-400 border border-dark-600 hover:text-white">Cancel</button>
        <button onClick={submit} disabled={!canSave || saving}
          className="px-4 py-2 rounded-xl text-sm font-semibold bg-accent-primary text-dark-900 hover:bg-accent-primary/90 disabled:opacity-40 flex items-center gap-2">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          {isEdit ? 'Update schedule' : 'Create schedule'}
        </button>
      </div>
    </Card>
  );
}

// ─── Scan / execute panel ────────────────────────────────────────────────────

function ScanPanel({ schedule, scan, onClose, onExecute, executing }) {
  const opp = scan?.opportunity;
  const meets = scan?.meetsThreshold;
  // Show price data whenever we have BOTH prices (even if not tradable after fees).
  // Previously this whole panel hid the prices when tradable=false, which made
  // "no tradable spread" look like a failure — users couldn't see WHY it wasn't
  // tradable. Now we always surface the real numbers so users can see how
  // close they are and learn the fee math.
  const hasPrices = opp?.buyPrice != null && opp?.sellPrice != null;
  const tickerError = opp?.reason && (opp.reason.includes('ticker') || opp.reason === 'missing-ticker');

  return (
    <Card>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider">Live spread — {schedule.name}</h3>
        <button onClick={onClose} className="text-gray-500 hover:text-white"><X className="w-4 h-4" /></button>
      </div>

      {!hasPrices ? (
        // Genuine failure — couldn't fetch tickers from one or both exchanges.
        <div className="bg-red-500/5 border border-red-500/15 rounded-xl px-4 py-3 flex items-start gap-3">
          <AlertTriangle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-red-300">Couldn't fetch live prices</p>
            <p className="text-xs text-gray-400 mt-0.5">{opp?.reason || scan?.reason || 'No data returned from one or both exchanges.'}</p>
            <p className="text-[10px] text-gray-500 mt-2">Common causes: API key permissions, exchange downtime, or the symbol isn't tradable on one side.</p>
          </div>
        </div>
      ) : (
        <>
          {/* Always show the real-time price comparison + spread math */}
          <div className="grid grid-cols-3 gap-3 mb-4">
            <div className="bg-dark-700/40 rounded-xl p-3">
              <p className="text-[10px] uppercase text-gray-500 mb-1">Buy on</p>
              <p className="text-sm font-bold text-emerald-400">{opp.buyExchange}</p>
              <p className="text-xs text-white mt-0.5">${Number(opp.buyPrice).toLocaleString(undefined, { maximumFractionDigits: 4 })}</p>
            </div>
            <div className="bg-dark-700/40 rounded-xl p-3">
              <p className="text-[10px] uppercase text-gray-500 mb-1">Sell on</p>
              <p className="text-sm font-bold text-red-400">{opp.sellExchange}</p>
              <p className="text-xs text-white mt-0.5">${Number(opp.sellPrice).toLocaleString(undefined, { maximumFractionDigits: 4 })}</p>
            </div>
            <div className={`rounded-xl p-3 border ${
              meets ? 'bg-emerald-500/10 border-emerald-500/30'
                    : opp.netSpreadPct >= 0 ? 'bg-amber-500/10 border-amber-500/30'
                    : 'bg-red-500/10 border-red-500/30'
            }`}>
              <p className="text-[10px] uppercase text-gray-500 mb-1">Net spread (after fees)</p>
              <p className={`text-sm font-bold ${
                meets ? 'text-emerald-400'
                      : opp.netSpreadPct >= 0 ? 'text-amber-400'
                      : 'text-red-400'
              }`}>
                {opp.netSpreadPct >= 0 ? '+' : ''}{opp.netSpreadPct?.toFixed(3)}%
              </p>
              <p className="text-[10px] text-gray-500 mt-0.5">Gross {opp.grossSpreadPct?.toFixed(3)}% − {(opp.takerFeePct * 2).toFixed(2)}% fees</p>
            </div>
          </div>

          {meets ? (
            <button onClick={onExecute} disabled={executing}
              className="w-full px-4 py-2.5 rounded-xl text-sm font-semibold bg-emerald-500 text-dark-900 hover:bg-emerald-400 disabled:opacity-40 flex items-center justify-center gap-2">
              {executing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
              {executing ? 'Firing both legs (IOC)…' : 'Execute now (real CCXT orders)'}
            </button>
          ) : (
            <div className="bg-amber-500/5 border border-amber-500/15 rounded-xl px-4 py-2.5">
              <p className="text-xs text-amber-300">
                {opp.netSpreadPct < 0 ? (
                  <>
                    <strong>Not tradable</strong> — net spread is <strong>{opp.netSpreadPct.toFixed(3)}%</strong> after fees.
                    Gross spread {Math.abs(opp.grossSpreadPct).toFixed(3)}% isn't enough to overcome {(opp.takerFeePct * 2).toFixed(2)}% in round-trip fees.
                  </>
                ) : (
                  <>
                    Net spread <strong>{opp.netSpreadPct.toFixed(3)}%</strong> is positive but below your <strong>{schedule.minSpreadPct}%</strong> threshold.
                    Lower the threshold if you want this to fire.
                  </>
                )}
              </p>
              <p className="text-[10px] text-gray-500 mt-1.5">
                This is normal — real-world cross-CEX spreads on major pairs are tiny (0.05–0.3% gross typically) and usually get eaten by fees. Arbitrage opportunities exist mostly on volatility spikes or illiquid pairs.
              </p>
            </div>
          )}
        </>
      )}
    </Card>
  );
}

// ─── History panel ───────────────────────────────────────────────────────────

function HistoryPanel({ history, schedule, onClose }) {
  return (
    <Card>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider">History — {schedule.name}</h3>
        <button onClick={onClose} className="text-gray-500 hover:text-white"><X className="w-4 h-4" /></button>
      </div>
      {(!history || history.length === 0) ? (
        <p className="text-gray-500 text-sm text-center py-6">No attempts recorded yet.</p>
      ) : (
        <div className="space-y-2 max-h-96 overflow-y-auto pr-1">
          {history.map(h => {
            const st = STATUS_STYLES[h.status] || STATUS_STYLES.error;
            const profitColor = (h.realizedUsd ?? 0) >= 0 ? 'text-emerald-400' : 'text-red-400';
            return (
              <div key={h.attemptId || h.startedAt} className="bg-dark-700/40 rounded-xl px-3 py-2 text-xs">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-gray-500">{new Date(h.startedAt).toLocaleString()}</span>
                  <span className={`font-bold px-2 py-0.5 rounded-full ${st.bg} ${st.color}`}>{st.label}</span>
                </div>
                {h.opportunity?.netSpreadPct != null && (
                  <p className="text-gray-400">
                    Net spread: <span className="text-white font-mono">{h.opportunity.netSpreadPct.toFixed(3)}%</span>
                    {h.opportunity.buyExchange && (
                      <> · Buy {h.opportunity.buyExchange} → Sell {h.opportunity.sellExchange}</>
                    )}
                  </p>
                )}
                {h.matchedAmount != null && (
                  <p className="text-gray-400">
                    Matched: <span className="text-white font-mono">{h.matchedAmount.toFixed(8)}</span>
                    {h.imbalanceAmount > 0 && (
                      <> · Imbalance: <span className="text-amber-400 font-mono">{h.imbalanceAmount.toFixed(8)}</span></>
                    )}
                  </p>
                )}
                {h.realizedUsd != null && (
                  <p className={`font-bold ${profitColor}`}>
                    P&L: ${(h.realizedUsd ?? 0) >= 0 ? '+' : ''}{(h.realizedUsd ?? 0).toFixed(2)}
                  </p>
                )}
                {h.reason && <p className="text-gray-600 mt-0.5">{h.reason}</p>}
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}

// ─── Schedule row ────────────────────────────────────────────────────────────

function ScheduleRow({ schedule, onScan, onEdit, onDelete, onHistory, busy }) {
  const status = schedule.lastRunStatus;
  const lastRun = schedule.lastRunAt ? new Date(schedule.lastRunAt).toLocaleString() : 'never';
  const st = STATUS_STYLES[status] || null;
  return (
    <Card>
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="text-white font-semibold truncate">{schedule.name}</h3>
            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${schedule.enabled ? 'bg-emerald-500/15 text-emerald-400' : 'bg-gray-500/15 text-gray-400'}`}>
              {schedule.enabled ? 'ENABLED' : 'PAUSED'}
            </span>
            <span className="text-[10px] text-gray-500 font-mono uppercase">{schedule.frequency}</span>
            {st && <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${st.bg} ${st.color}`}>{st.label}</span>}
          </div>
          <p className="text-xs text-gray-500 font-mono">{schedule.symbol} · {schedule.exchangeA} ↔ {schedule.exchangeB}</p>
          <p className="text-[10px] text-gray-600 mt-1">
            min spread {schedule.minSpreadPct}% · max ${schedule.maxPositionUsd} · {schedule.maxTradesPerHour}/hr · last run {lastRun}
          </p>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          <button onClick={() => onScan(schedule)} disabled={busy === schedule.scheduleId} title="Scan + execute"
            className="p-2 rounded-lg text-gray-400 hover:text-emerald-400 hover:bg-emerald-500/10 disabled:opacity-40">
            {busy === schedule.scheduleId ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Eye className="w-3.5 h-3.5" />}
          </button>
          <button onClick={() => onHistory(schedule)} title="History"
            className="p-2 rounded-lg text-gray-400 hover:text-white hover:bg-dark-700">
            <Clock className="w-3.5 h-3.5" />
          </button>
          <button onClick={() => onEdit(schedule)} title="Edit"
            className="p-2 rounded-lg text-gray-400 hover:text-white hover:bg-dark-700">
            <Save className="w-3.5 h-3.5" />
          </button>
          <button onClick={() => onDelete(schedule)} title="Delete"
            className="p-2 rounded-lg text-gray-400 hover:text-red-400 hover:bg-red-500/10">
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </Card>
  );
}

// ─── Main ────────────────────────────────────────────────────────────────────

export default function ArbitragePage() {
  const [schedules, setSchedules] = useState([]);
  const [exchanges, setExchanges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [scanFor, setScanFor] = useState(null);
  const [executing, setExecuting] = useState(false);
  const [historyFor, setHistoryFor] = useState(null);
  const [historyData, setHistoryData] = useState([]);
  const [toast, setToast] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all'); // 'all' | 'active' | 'paused'

  async function load(showSpinner = true) {
    if (showSpinner) setLoading(true);
    try {
      const [list, conn] = await Promise.all([
        listArbitrageSchedules().catch(() => null),
        getExchangeConnections().catch(() => null),
      ]);
      // Backend wraps arbitrage responses as { success, data: { schedules }, timestamp }.
      // Exchange connections returns flat { exchanges, connectedCount }. Handle both shapes.
      const schedulesArr = list?.data?.schedules || list?.schedules || [];
      const exchangesArr = conn?.exchanges || conn?.data?.exchanges || [];
      setSchedules(schedulesArr);
      setExchanges(exchangesArr.filter(e => e.connected));
    } catch (err) {
      if (showSpinner) setToast({ type: 'error', message: err.message || 'Failed to load' });
    }
    if (showSpinner) setLoading(false);
  }

  useEffect(() => { load(); }, []);

  // Auto-refresh every 15s when the list is visible (no modals open), so
  // worker-driven schedule updates (lastRunAt, lastRunStatus) surface without
  // a hard refresh — same pattern as Grid Bot / DCA Safety / Conditional.
  const isListVisible = !showNew && !editing && !scanFor && !historyFor;
  const pollInterval = useActivePolling(15000, isListVisible);
  useEffect(() => {
    if (!pollInterval) return;
    const id = setInterval(() => load(false), pollInterval);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pollInterval]);

  // Filter by enabled status. Counts shown on the filter pills.
  const filteredSchedules = useMemo(() => {
    if (statusFilter === 'active') return schedules.filter(s => s.enabled);
    if (statusFilter === 'paused') return schedules.filter(s => !s.enabled);
    return schedules;
  }, [schedules, statusFilter]);
  const activeCount = schedules.filter(s => s.enabled).length;
  const pausedCount = schedules.filter(s => !s.enabled).length;

  async function handleSave(payload) {
    setSaving(true);
    try {
      await saveArbitrageSchedule(payload);
      setToast({ type: 'success', message: payload.scheduleId ? 'Schedule updated' : 'Schedule created' });
      setShowNew(false);
      setEditing(null);
      await load();
    } catch (err) {
      setToast({ type: 'error', message: err.message || 'Save failed' });
    }
    setSaving(false);
  }

  async function handleDelete(schedule) {
    if (!window.confirm(`Delete "${schedule.name}"?`)) return;
    try {
      await deleteArbitrageSchedule(schedule.scheduleId);
      setToast({ type: 'success', message: 'Schedule deleted' });
      await load();
    } catch (err) {
      setToast({ type: 'error', message: err.message });
    }
  }

  async function handleScan(schedule) {
    setBusyId(schedule.scheduleId);
    try {
      const res = await scanArbitrageOpportunity(schedule.scheduleId);
      // Unwrap { success, data: { opportunity, meetsThreshold, reason }, timestamp }
      const scanData = res?.data || res;
      setScanFor({ schedule, scan: scanData });
    } catch (err) {
      setToast({ type: 'error', message: err.message || 'Scan failed' });
    }
    setBusyId(null);
  }

  async function handleExecuteFromScan() {
    if (!scanFor?.schedule) return;
    if (!window.confirm(`Fire real IOC orders on ${scanFor.schedule.exchangeA} + ${scanFor.schedule.exchangeB}?`)) return;
    setExecuting(true);
    try {
      const res = await executeArbitrageNow(scanFor.schedule.scheduleId);
      // Unwrap { success, data: summary, timestamp }
      const summary = res?.data || res;
      const st = STATUS_STYLES[summary.status] || { label: summary.status };
      const isError = ['error', 'open-leg'].includes(summary.status);
      const isWarning = ['partial', 'partial-leg', 'rate-limited', 'unfilled', 'no-trade'].includes(summary.status);
      setToast({
        type: isError ? 'error' : isWarning ? 'warning' : 'success',
        message: `Result: ${st.label}${summary.realizedUsd != null ? ` · P&L $${(summary.realizedUsd ?? 0).toFixed(2)}` : ''}${summary.reason ? ` — ${summary.reason}` : ''}`,
      });
      setScanFor(null);
      await load();
    } catch (err) {
      setToast({ type: 'error', message: err.message || 'Execute failed' });
    }
    setExecuting(false);
  }

  async function handleHistory(schedule) {
    setBusyId(schedule.scheduleId);
    try {
      const res = await getArbitrageHistory(schedule.scheduleId);
      // Unwrap { success, data: { history }, timestamp }
      const historyArr = res?.data?.history || res?.history || [];
      setHistoryFor(schedule);
      setHistoryData(historyArr);
    } catch (err) {
      setToast({ type: 'error', message: err.message });
    }
    setBusyId(null);
  }

  return (
    <FeatureGate feature="arbitrageDetector">
      <div className="space-y-6 animate-fade-in">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold text-accent-primary flex items-center gap-2">
              <Zap className="w-6 h-6" /> Cross-Exchange Arbitrage
            </h1>
            <p className="text-gray-400 text-sm mt-1">
              Detect price spreads on the same symbol across two of your connected CEXes. Fire IOC buy + sell legs in parallel when the net spread (after fees) clears your threshold.
            </p>
          </div>
          <button onClick={() => { setEditing(null); setShowNew(true); }}
            disabled={exchanges.length < 2}
            className="px-4 py-2 rounded-xl text-sm font-semibold bg-accent-primary text-dark-900 hover:bg-accent-primary/90 disabled:opacity-40 flex items-center gap-2">
            <Plus className="w-4 h-4" /> New schedule
          </button>
        </div>

        {!loading && exchanges.length < 2 && (
          <div className="bg-amber-500/5 border border-amber-500/20 rounded-2xl px-4 py-3 flex items-start gap-3">
            <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-amber-300">Connect at least two CEXes</p>
              <p className="text-xs text-amber-300/80 mt-0.5">Cross-exchange arbitrage needs two connected CEXes to compare. Settings → Connections.</p>
            </div>
          </div>
        )}

        {(showNew || editing) && (
          <ScheduleEditor
            schedule={editing}
            exchanges={exchanges}
            saving={saving}
            onCancel={() => { setShowNew(false); setEditing(null); }}
            onSubmit={handleSave}
          />
        )}

        {scanFor && (
          <ScanPanel
            schedule={scanFor.schedule}
            scan={scanFor.scan}
            executing={executing}
            onClose={() => setScanFor(null)}
            onExecute={handleExecuteFromScan}
          />
        )}

        {historyFor && (
          <HistoryPanel schedule={historyFor} history={historyData} onClose={() => { setHistoryFor(null); setHistoryData([]); }} />
        )}

        {loading ? (
          <div className="flex items-center justify-center py-20"><Spinner className="text-accent-primary" /></div>
        ) : schedules.length === 0 ? (
          <Card className="text-center py-16">
            <Zap className="w-10 h-10 text-gray-600 mx-auto mb-3" />
            <p className="text-gray-400 text-sm">No arbitrage schedules yet.</p>
            <p className="text-gray-600 text-xs mt-1">Create one to scan + execute spreads automatically.</p>
          </Card>
        ) : (
          <>
            {/* Active / Paused / All filter — matches Grid Bot / DCA Safety / Conditional pattern.
                Classes are explicit strings so Tailwind's JIT compiles them. */}
            <div className="flex items-center gap-2 text-xs">
              {[
                { id: 'active', label: 'Active', count: activeCount,        activeClass: 'bg-emerald-500/15 border-emerald-500/40 text-emerald-300' },
                { id: 'paused', label: 'Paused', count: pausedCount,        activeClass: 'bg-amber-500/15 border-amber-500/40 text-amber-300' },
                { id: 'all',    label: 'All',    count: schedules.length,   activeClass: 'bg-gray-500/15 border-gray-500/40 text-gray-200' },
              ].map(f => (
                <button
                  key={f.id}
                  onClick={() => setStatusFilter(f.id)}
                  className={`px-3 py-1.5 rounded-lg border transition-colors ${
                    statusFilter === f.id ? f.activeClass : 'bg-dark-700 border-dark-600 text-gray-400 hover:text-white'
                  }`}
                >
                  {f.label} <span className="ml-1 text-[10px] opacity-70">{f.count}</span>
                </button>
              ))}
              <span className="ml-auto text-[10px] text-gray-500">Auto-refresh every 15s</span>
            </div>

            {filteredSchedules.length === 0 ? (
              <Card className="text-center py-8">
                <p className="text-gray-500 text-sm">No {statusFilter} schedules.</p>
              </Card>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {filteredSchedules.map(s => (
                  <ScheduleRow
                    key={s.scheduleId}
                    schedule={s}
                    busy={busyId}
                    onScan={handleScan}
                    onEdit={(sched) => { setShowNew(false); setEditing(sched); }}
                    onDelete={handleDelete}
                    onHistory={handleHistory}
                  />
                ))}
              </div>
            )}
          </>
        )}

        <div className="flex justify-end">
          <button onClick={load} className="text-xs text-gray-500 hover:text-white flex items-center gap-1">
            <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </button>
        </div>

        {toast && <Toast {...toast} onDismiss={() => setToast(null)} />}
      </div>
    </FeatureGate>
  );
}
